import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { addDays, type ISODate } from '@app/shared'

import type { MailTriageItem, MailVerdict } from '../../lib/data/types'
import {
  monthGrid,
  pickedPhrase,
  pickedSnoozeUntil,
  snoozeChoices,
  type SnoozeChoice,
  type SnoozeTarget,
} from './designed'
import { CalendarGlyph, ChevronLeft, ChevronRight, Minus, Plus } from './icons'
import { Kc, Tip, usePresence } from './parts'

// ============================================================================
// THE TWO MENUS (Components + Keyboard boards): Snooze — three presets and a date — and the
// sender rules from the ⋯ button. Both open with a 4px drop and leave faster than they came (14h).
// ============================================================================

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export function MenuShell({
  open,
  width,
  align = 'left',
  label,
  children,
}: {
  open: boolean
  width: number
  align?: 'left' | 'right'
  label: string
  children: React.ReactNode
}) {
  const { mounted, closing } = usePresence(open)
  if (!mounted) return null
  return (
    <div
      role="menu"
      aria-label={label}
      data-closing={closing || undefined}
      className={clsx(
        'day-menu absolute top-[calc(100%+6px)] z-30 flex flex-col gap-px rounded-panel border border-line bg-surface p-[5px] shadow-frame',
        align === 'right' ? 'right-0' : 'left-0',
      )}
      style={{ width }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  )
}

function MenuItem({ children, onClick, k }: { children: React.ReactNode; onClick: () => void; k?: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="day-menu-item"
    >
      {children}
      {k && <Kc>{k}</Kc>}
    </button>
  )
}

/** "Snooze until" — Tonight · Tomorrow morning · Next week, and the calendar (C). */
export function SnoozeMenu({
  open,
  picker,
  today,
  nowMinutes,
  target,
  pick,
  onPreset,
  onPicked,
  onPick,
  onOpenPicker,
  onClose,
  align,
}: {
  open: boolean
  picker: boolean
  today: ISODate
  nowMinutes: number
  target: SnoozeTarget
  pick: ISODate
  onPreset: (c: SnoozeChoice) => void
  onPicked: (until: string) => void
  onPick: (date: ISODate) => void
  onOpenPicker: () => void
  onClose: () => void
  align?: 'left' | 'right'
}) {
  return (
    <MenuShell open={open} width={picker ? 300 : 280} align={align} label="Snooze until">
      {picker ? (
        <DatePicker today={today} target={target} pick={pick} onPick={onPick} onSnooze={onPicked} onBack={onClose} />
      ) : (
        <>
          <div className="mb-1 flex items-center gap-2 border-b border-line-3 pb-[6px] pl-[10px] pr-1 pt-1">
            <span className="flex-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">Snooze until</span>
            <button type="button" aria-label="Pick a date" onClick={onOpenPicker} className="day-btn day-tipw h-7 gap-[5px] px-[7px]">
              <CalendarGlyph size={14} />
              <Kc>C</Kc>
              <Tip name="Pick a date" k="C" align="right">
                Mail comes back at the time it arrived; a reminder or an assignment at 8:00 AM.
              </Tip>
            </button>
          </div>
          {snoozeChoices(today, nowMinutes).map((c) => (
            <MenuItem key={c.key} onClick={() => onPreset(c)} k={String(c.key)}>
              <span className="flex-1">{c.label}</span>
              <span className="text-[11.5px] text-microcopy">{c.detail}</span>
            </MenuItem>
          ))}
        </>
      )}
    </MenuShell>
  )
}

/**
 * THE DATE PICKER (Keyboard board). Arrow keys move the day, Enter snoozes, past days are inactive.
 * Mail comes back at the time it arrived; a reminder or an assignment at 8:00 AM.
 */
export function DatePicker({
  today,
  target,
  pick,
  onPick,
  onSnooze,
  onBack,
}: {
  today: ISODate
  target: SnoozeTarget
  pick: ISODate
  onPick: (date: ISODate) => void
  onSnooze: (until: string) => void
  onBack: () => void
}) {
  const [month, setMonth] = useState<ISODate>(`${pick.slice(0, 7)}-01` as ISODate)
  // The month follows the selection when the arrow keys walk off its edge.
  useEffect(() => {
    if (pick.slice(0, 7) !== month.slice(0, 7)) setMonth(`${pick.slice(0, 7)}-01` as ISODate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick])
  const selected = pick
  const until = pickedSnoozeUntil(selected, target)
  const [y, m] = month.split('-').map(Number)
  return (
    <div className="flex flex-col" data-day-picker>
      <div className="flex items-center gap-2 border-b border-line-3 pb-[6px] pl-[10px] pr-1 pt-1">
        <button type="button" aria-label="Back to presets" onClick={onBack} className="day-btn h-[26px] w-[26px] justify-center p-0">
          <ChevronLeft size={14} />
        </button>
        <span className="flex-1 text-[12.5px] font-semibold text-ink">
          {MONTHS[m! - 1]} {y}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setMonth((cur) => `${addDays(cur, 32).slice(0, 7)}-01` as ISODate)}
          className="day-btn h-[26px] w-[26px] justify-center p-0"
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-[2px] px-2 pb-2 pt-1" role="grid">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className="py-1 text-center text-[10px] font-semibold text-muted">
            {d}
          </span>
        ))}
        {monthGrid(month).map(({ date, inMonth }) => {
          const past = date < today
          const isSel = date === selected
          return (
            <button
              key={date}
              type="button"
              disabled={!inMonth || past}
              aria-label={date}
              aria-pressed={isSel}
              onClick={() => onPick(date)}
              data-day-date={date}
              // ONE colour set per state, never two that Tailwind would have to order: the board's
              // `.d` · `.d-out` / `.d-past` · `.d-today` · `.d-sel`.
              className={clsx(
                'h-[30px] rounded-button border-0 text-[12px]',
                isSel
                  ? 'cursor-pointer bg-ink font-semibold text-page'
                  : !inMonth || past
                    ? 'cursor-default bg-transparent text-faint'
                    : date === today
                      ? 'cursor-pointer bg-transparent text-ink shadow-[inset_0_0_0_1px_var(--color-line-2)] hover:bg-surface-4'
                      : 'cursor-pointer bg-transparent text-ink-2 hover:bg-surface-4',
              )}
            >
              {Number(date.slice(8))}
            </button>
          )
        })}
      </div>
      <div className="flex flex-col gap-[10px] border-t border-line-3 px-[10px] pb-[6px] pt-[10px]">
        <span className="text-[12px] leading-[1.45] text-ink-2">
          Comes back <b className="font-semibold text-ink">{pickedPhrase(until)}</b>
          {target.kind === 'mail' ? ', the time it arrived.' : '.'}
        </span>
        <div className="flex justify-end gap-[6px]">
          <button type="button" className="day-btn" onClick={onBack}>
            Cancel
          </button>
          <button type="button" className="day-btn day-btn-solid" onClick={() => onSnooze(until)}>
            Snooze
          </button>
        </div>
      </div>
    </div>
  )
}

/** "Always important from …" / "Always noise from …" — the ⋯ button's menu. */
export function RulesMenu({
  open,
  item,
  onRule,
}: {
  open: boolean
  item: Pick<MailTriageItem, 'from'>
  onRule: (verdict: MailVerdict) => void
}) {
  const address = item.from.address.toLowerCase()
  return (
    <MenuShell open={open} width={340} align="right" label="Rules for this sender">
      <MenuItem onClick={() => onRule('important')}>
        <Plus size={14} className="text-accent" />
        <span className="min-w-0 flex-1 truncate">Always important from {address}</span>
      </MenuItem>
      <MenuItem onClick={() => onRule('noise')}>
        <Minus size={14} className="text-muted" />
        <span className="min-w-0 flex-1 truncate">Always noise from {address}</span>
      </MenuItem>
      <div className="mt-1 border-t border-line-3 px-[10px] pb-[6px] pt-2 text-[11px] leading-[1.45] text-microcopy">
        Your rules override Claude's sorting. Nothing is changed in Gmail.
      </div>
    </MenuShell>
  )
}

/** Keyboard movement inside the picker: ±1 day, ±7, never before today. */
export function stepPick(pick: ISODate, key: string, today: ISODate): ISODate {
  const delta = key === 'ArrowLeft' ? -1 : key === 'ArrowRight' ? 1 : key === 'ArrowUp' ? -7 : key === 'ArrowDown' ? 7 : 0
  const next = addDays(pick, delta)
  return next < today ? today : next
}
